/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Paper, TableRow } from '@mui/material';
import React, { useMemo, useState } from 'react';

import {
  DataTypeProvider,
  FilteringState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SortingState
} from '@devexpress/dx-react-grid';
import { Grid, PagingPanel, Table, TableFilterRow, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { getMonth } from 'modules/common/utils';

const ActivityLogView = ({ logs, enumerations }) => {
  const renderHeaderRow = row => {
    return (
      <TableRow sx={{ backgroundColor: '#41B7C0', '& .MuiButtonBase-root': { color: 'white !important' } }}>
        {row.children.map(c => c)}
      </TableRow>
    );
  };

  const formatDate = value => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const formatter = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' });
    return `${getMonth(date.getMonth())} ${date.getDate()} ${date.getFullYear()} ${formatter.format(date)}`;
  };

  const enumTitleById = useMemo(() => {
    const map = new Map();

    if (!enumerations) return map;

    Object.keys(enumerations).forEach(key => {
      const list = enumerations[key] || [];
      for (const opt of list) {
        const id = opt?.id ?? opt?.value ?? opt?.enumerationOptionId;
        const title = opt?.title ?? opt?.name ?? opt?.label;

        if (id != null && title != null && !map.has(String(id))) {
          map.set(String(id), title);
        }
      }
    });

    return map;
  }, [enumerations]);

  const resolveEnumTitle = val => {
    if (val == null) return null;
    return enumTitleById.get(String(val)) ?? null;
  };

  const rows = useMemo(() => {
    const base = Array.isArray(logs) ? logs : [];

    const sorted = [...base].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));

    return sorted.map(l => {
      if (!l?.isEnumeration) {
        return {
          ...l,
          displayFieldOldValue: l?.oldValue ?? null,
          displayFieldNewValue: l?.newValue ?? null
        };
      }

      const oldTitle = resolveEnumTitle(l.oldValue);
      const newTitle = resolveEnumTitle(l.newValue);

      return {
        ...l,
        displayFieldOldValue: oldTitle ?? l.oldValue ?? null,
        displayFieldNewValue: newTitle ?? l.newValue ?? null
      };
    });
  }, [logs, enumTitleById]);

  const [sorting, setSorting] = useState([{ columnName: 'changedAt', direction: 'desc' }]);

  const [columns] = useState([
    { name: 'changedAt', title: 'Date' },
    { name: 'changedById', title: 'User' },
    { name: 'operationType', title: 'Type' },
    { name: 'tableName', title: 'Table' },
    { name: 'columnName', title: 'Column' },
    { name: 'displayFieldOldValue', title: 'Old Value' },
    { name: 'displayFieldNewValue', title: 'New Value' }
  ]);

  const [tableColumnExtensions] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [pageSizes] = useState([15, 20, 25]);
  const [filters, setFilters] = useState([]);

  return (
    <Box>
      <Paper>
        <Grid rows={rows} columns={columns}>
          <DataTypeProvider for={['changedAt']} formatterComponent={({ value }) => formatDate(value)} />
          <SortingState sorting={sorting} onSortingChange={setSorting} />
          <IntegratedSorting columnExtensions={[{
            columnName: 'changedAt',
            compare: (first, second) => (new Date(first).getTime() || 0) - (new Date(second).getTime() || 0)
          }]} />
          <PagingState currentPage={currentPage} onCurrentPageChange={setCurrentPage} pageSize={pageSize} onPageSizeChange={setPageSize} />
          <FilteringState filters={filters} onFiltersChange={setFilters} />
          <IntegratedFiltering columnExtensions={[{
            columnName: 'changedAt',
            predicate: (value, filter) => IntegratedFiltering.defaultPredicate(formatDate(value), filter)
          }]} />
          <IntegratedPaging />
          <Table columnExtensions={tableColumnExtensions} />
          <TableHeaderRow rowComponent={renderHeaderRow} showSortingControls />
          <TableFilterRow />
          <PagingPanel pageSizes={pageSizes} />
        </Grid>
      </Paper>
    </Box>
  );
};

export default ActivityLogView;
